import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProducts } from '../context/ProductContext';
import { useCategories } from '../context/CategoryContext';
import { useLocations } from '../context/LocationContext';
import { Product, PartnerLink, PartnerAvailability, PublishStatus } from '@tourstream/shared';
import { api } from '../utils/api';
import { useUnsavedChangesGuard } from '../components/AdminLayout';

const KNOWN_PARTNER_NAMES = ['마이리얼트립', 'KLOOK', 'KKday', '와그', 'GetYourGuide', '트립닷컴', '쿠팡'];

// API 연동으로 검색 가능한 파트너 목록. 나중에 파트너가 늘어나면 여기에만 추가하면 됨.
const PARTNER_API_OPTIONS = [{ key: 'myrealtrip', label: '마이리얼트립' }];

// KLOOK/KKday는 검색 API는 없지만, URL에 고정 파라미터만 붙이면 트래킹 링크가 되어서
// 서버가 상품 저장 시 파트너명을 보고 자동으로 변환해줌 (어드민에서 따로 버튼 누를 필요 없음).

const emptyPartnerLink = (): PartnerLink => ({ partner: '', url: '', source: 'manual' });

// 공개 상태 안내 (손님에게 보일지). 판매처 예약 가능 여부와는 따로 정함
const PUBLISH_OPTIONS: Array<{ value: Exclude<PublishStatus, 'needs_classification'>; label: string; help: string }> = [
  { value: 'draft', label: '초안', help: '작성·수집·검수 중. 관리자만 볼 수 있어요' },
  { value: 'published', label: '공개', help: '손님에게 보여요. 이름·실제 사진·https 판매처 링크가 있어야 해요' },
  { value: 'hidden', label: '숨김', help: '정보 오류 등으로 공개를 멈춰요. 판매처가 다시 팔아도 자동으로 공개되지 않아요' },
];
const AVAILABILITY_LABEL: Record<string, string> = { '': '기록 안 함', available: '예약 가능', unavailable: '예약 불가', unknown: '확인 못 함' };
const UNAVAILABLE_REASONS: Record<string, string> = { temporary_soldout: '일시 품절', sales_stopped: '판매 중지', sales_ended: '판매 종료 (근거 필요)' };
const UNKNOWN_REASONS: Record<string, string> = { not_checked: '아직 확인 안 함', access_failed: '접근 실패', captcha: 'CAPTCHA', price_missing: '가격 누락', other: '기타' };
const splitList = (text: string) => text.split(',').map((v) => v.trim()).filter(Boolean);

interface PartnerSearchResult {
  externalId: string;
  name: string;
  price?: number;
  priceDisplay?: string;
  url: string;
  thumbnail?: string;
  rating?: number;
  reviewCount?: number;
}

interface PartnerSearchState {
  index: number;
  partnerKey: string;
  keyword: string;
  results: PartnerSearchResult[];
  loading: boolean;
  error: string;
}

const parseOptionalNumber = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

function buildFormStateFromProduct(product: Product) {
  const images = Array.isArray(product.images) ? product.images : [];
  const categories = Array.isArray(product.categories) ? product.categories : [];
  const locations = Array.isArray(product.locations) ? product.locations : [];
  const tags = Array.isArray(product.tags) ? product.tags : [];
  return {
    name: product.name || '',
    description: product.description || '',
    price: product.price !== undefined ? String(product.price) : '',
    rating: product.rating !== undefined ? String(product.rating) : '',
    reviewCount: product.reviewCount !== undefined ? String(product.reviewCount) : '',
    images: images.length > 0 ? images : [''],
    categories,
    locations,
    tags,
    isRecommended: Boolean(product.isRecommended),
    isAvailable: product.isAvailable !== false,
    publishStatus: (product.publishStatus || (product.isAvailable === false ? 'needs_classification' : 'published')) as PublishStatus,
    partnerLinks: Array.isArray(product.partnerLinks) && product.partnerLinks.length > 0
      ? product.partnerLinks
      : [emptyPartnerLink()],
    baseRev: Number(product.rev) || 0, // 불러온 저장 번호 — 저장할 때 보내서, 그사이 바뀌었으면 서버가 거절(409)
  };
}

export default function ProductFormPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { addProduct, updateProduct, getProduct } = useProducts();
  const { mainCategories, getSubCategoriesByMain } = useCategories();
  const { countries, getRegionsByCountry } = useLocations();

  const isEdit = !!id;
  const product = id ? getProduct(id) : null;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    rating: '',
    reviewCount: '',
    images: [''],
    categories: [] as string[],
    locations: [] as string[],
    tags: [] as string[],
    isRecommended: false,
    isAvailable: false,
    publishStatus: 'draft' as PublishStatus, // 새 상품은 초안으로 시작 (검수 후 공개)
    partnerLinks: [emptyPartnerLink()] as PartnerLink[],
    baseRev: 0,
  });

  const [tagInput, setTagInput] = useState('');
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify(formData));
  useUnsavedChangesGuard(JSON.stringify(formData) !== savedSnapshot || tagInput.trim() !== '');

  const [categorySearch, setCategorySearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [expandedMainCategories, setExpandedMainCategories] = useState<Set<string>>(new Set());
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(new Set());
  const [partnerSearch, setPartnerSearch] = useState<PartnerSearchState | null>(null);

  useEffect(() => {
    if (product) {
      const next = buildFormStateFromProduct(product);
      setFormData(next);
      setSavedSnapshot(JSON.stringify(next));
    }
  }, [product, id]);

  // 목록 로드 전에 상세 URL로 진입한 경우 등: 컨텍스트에 없으면 API에서 직접 로드
  useEffect(() => {
    if (!isEdit || !id || product) return;
    let cancelled = false;
    (async () => {
      try {
        const p = (await api.getProduct(id)) as Product;
        if (!cancelled && p) {
          const next = buildFormStateFromProduct(p);
          setFormData(next);
          setSavedSnapshot(JSON.stringify(next));
        }
      } catch {
        // 컨텍스트 동기화 후에도 없으면 사용자가 저장 시도 시 서버 메시지로 안내
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, id, product]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.categories.length === 0) {
      alert('카테고리를 최소 1개 이상 선택해주세요.');
      return;
    }

    if (formData.locations.length === 0) {
      alert('지역을 최소 1개 이상 선택해주세요.');
      return;
    }

    const partnerLinks = formData.partnerLinks
      .map((link) => ({ ...link, partner: link.partner.trim(), url: link.url.trim() }))
      .filter((link) => link.url !== '');
    if (partnerLinks.length === 0) {
      alert('예약 URL을 최소 1개 이상 입력해주세요.');
      return;
    }

    const productData: Omit<Product, 'id' | 'views'> = {
      name: formData.name,
      description: formData.description,
      // 빈 칸 = 지우기(null). 서버는 칸을 안 보내면 그대로 두기 때문에 지울 때는 null을 보냄
      price: formData.price.trim() === '' ? null : parseOptionalNumber(formData.price),
      rating: formData.rating.trim() === '' ? null : parseOptionalNumber(formData.rating),
      reviewCount: formData.reviewCount.trim() === '' ? null : parseOptionalNumber(formData.reviewCount),
      images: formData.images.filter(img => img.trim() !== ''),
      categories: formData.categories,
      locations: formData.locations,
      tags: formData.tags,
      isRecommended: formData.isRecommended,
      isAvailable: formData.publishStatus === 'published',
      // '분류 필요'는 관리자가 정하기 전까지 그대로 둔다 (서버가 받는 값은 초안·공개·숨김)
      ...(formData.publishStatus !== 'needs_classification' ? { publishStatus: formData.publishStatus } : {}),
      partnerLinks,
    } as any;

    try {
      if (isEdit && id) {
        await updateProduct(id, { ...productData, baseRev: formData.baseRev } as any);
      } else {
        await addProduct(productData);
      }
      navigate('/products');
    } catch (error: any) {
      console.error('상품 저장 실패:', error);
      const message = typeof error?.message === 'string' && error.message.trim()
        ? error.message
        : '상품 저장에 실패했습니다. 다시 시도해주세요.';
      alert(message);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  // 판매처 예약 상태 바꾸기. 확인 시각은 '상태를 바꿨을 때'와 '지금 다시 확인함'을 눌렀을 때만 지금으로 (범위·근거 문구만 고치면 예전 확인 시각 유지 — 예전 확인을 최신처럼 보이지 않게)
  // 확인 시각이 없던 기록도 문구만 고치면 '확인 시각 없음' 그대로 (수정 시각 updatedAt만 남김)
  // ('지금 다시 확인함'은 patch.checkedAt = 'now'로 알림)
  const updateAvailability = (index: number, patch: Partial<PartnerAvailability> | null) => {
    const partnerLinks = formData.partnerLinks.map((link, i) => {
      if (i !== index) return link;
      if (patch === null) {
        const { availability, ...rest } = link;
        return rest;
      }
      const prev = link.availability || { status: 'unknown' as const };
      const reconfirm = patch.checkedAt === 'now';
      const statusChanged = patch.status !== undefined && patch.status !== prev.status;
      const next: PartnerAvailability = { ...prev, ...patch, checkedAt: prev.checkedAt };
      const now = new Date().toISOString();
      next.updatedAt = now;
      if (statusChanged || reconfirm) {
        next.checkedAt = now;
        next.source = '관리자 직접 확인';
      }
      return { ...link, availability: next };
    });
    setFormData({ ...formData, partnerLinks });
  };

  const addPartnerLink = () => {
    setFormData({ ...formData, partnerLinks: [...formData.partnerLinks, emptyPartnerLink()] });
  };

  const removePartnerLink = (index: number) => {
    setFormData({ ...formData, partnerLinks: formData.partnerLinks.filter((_, i) => i !== index) });
  };

  // 가격 값을 새로 넣으면 그때가 가격 확인 시각 (통화가 없으면 원화). 0·빈 값은 가격 없음 → 통화·조건·확인 시각도 지움
  const updatePartnerLink = (index: number, field: 'partner' | 'url' | 'price' | 'priceCurrency' | 'priceConditions', value: string) => {
    const partnerLinks = formData.partnerLinks.map((link, i) => {
      if (i !== index) return link;
      if (field === 'price') {
        const parsed = parseOptionalNumber(value);
        if (parsed === undefined || parsed <= 0) {
          // 가격을 비우면 '삭제'를 서버에 알림 (null) — 칸을 빼면 서버는 예전 가격을 그대로 둠
          const { priceCurrency, priceConditions, priceCheckedAt, priceDisplay, ...rest } = link;
          return { ...rest, price: null };
        }
        // 새 가격 = 새 확인: 통화·조건(없으면 빈 글자)·확인 시각을 함께
        return { ...link, price: parsed, priceCurrency: link.priceCurrency || 'KRW', priceConditions: link.priceConditions ?? '', priceCheckedAt: new Date().toISOString() };
      }
      // 통화·조건을 바꾸는 것도 새 확인 (지금 확인한 것으로)
      if (field === 'priceCurrency') return { ...link, priceCurrency: value.trim().toUpperCase(), priceConditions: link.priceConditions ?? '', priceCheckedAt: new Date().toISOString() };
      if (field === 'priceConditions') return { ...link, priceConditions: value, priceCheckedAt: new Date().toISOString() };
      return { ...link, [field]: value };
    });
    setFormData({ ...formData, partnerLinks });
  };

  const openPartnerSearch = (index: number, partnerKey: string) => {
    setPartnerSearch({ index, partnerKey, keyword: formData.partnerLinks[index]?.partner || '', results: [], loading: false, error: '' });
  };

  const closePartnerSearch = () => setPartnerSearch(null);

  const runPartnerSearch = async () => {
    if (!partnerSearch || !partnerSearch.keyword.trim()) return;
    setPartnerSearch({ ...partnerSearch, loading: true, error: '' });
    try {
      const results = await api.searchPartnerProducts(partnerSearch.partnerKey, partnerSearch.keyword.trim());
      setPartnerSearch((prev) => (prev ? { ...prev, results, loading: false } : prev));
    } catch (error: any) {
      setPartnerSearch((prev) => (prev ? { ...prev, loading: false, error: error?.message || '검색에 실패했습니다' } : prev));
    }
  };

  const selectPartnerSearchResult = async (result: PartnerSearchResult) => {
    if (!partnerSearch) return;
    const { index, partnerKey } = partnerSearch;
    const partnerLabel = PARTNER_API_OPTIONS.find((option) => option.key === partnerKey)?.label || partnerKey;

    let trackedUrl = result.url;
    try {
      trackedUrl = await api.createPartnerTrackedLink(partnerKey, result.url);
    } catch (error) {
      console.error('[ProductFormPage] 추적 링크 생성 실패, 원본 URL 사용:', error);
    }

    const partnerLinks = formData.partnerLinks.map((link, i) =>
      i === index
        ? {
            partner: partnerLabel,
            url: trackedUrl,
            source: 'api' as const,
            externalId: result.externalId,
            // 판매처 검색으로 찾은 가격 = 지금 확인한 가격 묶음 (원화, 판매처 표시 가격)
            ...(result.price !== undefined && result.price !== null && result.price > 0
              ? { price: result.price, priceCurrency: 'KRW', priceConditions: '판매처 표시 가격', priceCheckedAt: new Date().toISOString(), priceDisplay: result.priceDisplay }
              : { price: null }),
            updatedAt: new Date().toISOString(),
          }
        : link,
    );

    // 아직 이미지를 하나도 안 넣었으면 검색 결과의 썸네일을 기본값으로 채워줌 (직접 입력한 이미지는 덮어쓰지 않음)
    const hasImage = formData.images.some((img) => img.trim() !== '');
    const images = !hasImage && result.thumbnail ? [result.thumbnail] : formData.images;

    setFormData({ ...formData, partnerLinks, images });
    closePartnerSearch();
  };

  const disconnectPartnerApi = (index: number) => {
    const partnerLinks = formData.partnerLinks.map((link, i) => {
      if (i !== index) return link;
      // 연동 해제 = 외부 번호·가격을 지운다고 서버에 명시 (칸을 빼면 서버는 같은 판매처의 예전 값을 그대로 둠)
      const { source, externalId, price, priceDisplay, priceCurrency, priceConditions, priceCheckedAt, updatedAt, ...rest } = link;
      return { ...rest, source: 'manual' as const, externalId: '', price: null };
    });
    setFormData({ ...formData, partnerLinks });
  };

  const toggleCategory = (category: string) => {
    if (formData.categories.includes(category)) {
      setFormData({ ...formData, categories: formData.categories.filter(c => c !== category) });
    } else {
      setFormData({ ...formData, categories: [...formData.categories, category] });
    }
  };

  const toggleLocation = (location: string) => {
    if (formData.locations.includes(location)) {
      setFormData({ ...formData, locations: formData.locations.filter(l => l !== location) });
    } else {
      setFormData({ ...formData, locations: [...formData.locations, location] });
    }
  };

  const toggleMainCategory = (mainId: string) => {
    const newExpanded = new Set(expandedMainCategories);
    if (newExpanded.has(mainId)) {
      newExpanded.delete(mainId);
    } else {
      newExpanded.add(mainId);
    }
    setExpandedMainCategories(newExpanded);
  };

  const toggleCountry = (countryId: string) => {
    const newExpanded = new Set(expandedCountries);
    if (newExpanded.has(countryId)) {
      newExpanded.delete(countryId);
    } else {
      newExpanded.add(countryId);
    }
    setExpandedCountries(newExpanded);
  };

  // 검색 필터링된 카테고리
  const filteredMainCategories = useMemo(() => {
    if (!categorySearch.trim()) return mainCategories;
    
    const searchLower = categorySearch.toLowerCase();
    return mainCategories.filter(main => {
      const mainMatch = main.name.toLowerCase().includes(searchLower);
      const subs = getSubCategoriesByMain(main.id);
      const subMatch = subs.some(sub => sub.name.toLowerCase().includes(searchLower));
      return mainMatch || subMatch;
    });
  }, [mainCategories, categorySearch, getSubCategoriesByMain]);

  // 검색 필터링된 국가
  const filteredCountries = useMemo(() => {
    if (!locationSearch.trim()) return countries;
    
    const searchLower = locationSearch.toLowerCase();
    return countries.filter(country => {
      const countryMatch = country.name.toLowerCase().includes(searchLower);
      const regs = getRegionsByCountry(country.id);
      const regionMatch = regs.some(region => region.name.toLowerCase().includes(searchLower));
      return countryMatch || regionMatch;
    });
  }, [countries, locationSearch, getRegionsByCountry]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {isEdit ? '상품 수정' : '상품 등록'}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* 상품명 */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            상품명 *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        {/* 설명 */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            설명 *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        {/* 가격/평점 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              대표 가격 (원)
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="예: 55000"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              평점 (선택)
            </label>
            <input
              type="number"
              min="0"
              max="5"
              step="0.1"
              value={formData.rating}
              onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="예: 4.8"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              리뷰 수 (선택)
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.reviewCount}
              onChange={(e) => setFormData({ ...formData, reviewCount: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="예: 186"
            />
            <p className="mt-1 text-[11px] text-gray-400">평점을 입력하려면 리뷰 수도 함께 입력해야 화면에 표시돼요.</p>
          </div>
        </div>

        {/* 이미지 */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            이미지
          </label>
          <div className="space-y-3">
            {formData.images.map((image, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4">
                <div className="flex gap-2 mb-2">
                  {/* 파일 업로드 */}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const base64String = reader.result as string;
                          const newImages = [...formData.images];
                          newImages[index] = base64String;
                          setFormData({ ...formData, images: newImages });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                  />
                  {formData.images.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newImages = formData.images.filter((_, i) => i !== index);
                        setFormData({ ...formData, images: newImages });
                      }}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-xs"
                    >
                      삭제
                    </button>
                  )}
                </div>
                {/* 이미지 미리보기 */}
                {image && (
                  <div className="mt-2">
                    <img
                      src={image}
                      alt={`미리보기 ${index + 1}`}
                      className="max-w-xs max-h-32 object-cover rounded border border-gray-200"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                {/* URL 입력 (선택사항) */}
                <div className="mt-2">
                  <input
                    type="text"
                    value={image.startsWith('data:') ? '' : image}
                    onChange={(e) => {
                      const newImages = [...formData.images];
                      newImages[index] = e.target.value;
                      setFormData({ ...formData, images: newImages });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                    placeholder="또는 이미지 URL 입력"
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, images: [...formData.images, ''] })}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs"
            >
              이미지 추가
            </button>
          </div>
        </div>

        {/* 카테고리 - 검색 + 아코디언 */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            카테고리
          </label>
          <div className="mb-3">
            <input
              type="text"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="카테고리 검색..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="border border-gray-300 rounded-lg p-4 max-h-96 overflow-y-auto">
            {filteredMainCategories.length > 0 ? (
              <div className="space-y-2">
                {filteredMainCategories.map((main) => {
                  const subs = getSubCategoriesByMain(main.id);
                  const isExpanded = expandedMainCategories.has(main.id);
                  const searchLower = categorySearch.toLowerCase();
                  const filteredSubs = subs.filter(sub =>
                    !categorySearch || sub.name.toLowerCase().includes(searchLower)
                  );
                  const mainSelected = formData.categories.includes(main.name);

                  return (
                    <div key={main.id} className="border-b border-gray-200 last:border-b-0 pb-2 last:pb-0">
                      {/* 대분류 */}
                      <div className="flex items-center gap-2 mb-1">
                        <button
                          type="button"
                          onClick={() => toggleMainCategory(main.id)}
                          className="flex items-center gap-2 flex-1 text-left hover:bg-gray-50 px-2 py-1 rounded"
                        >
                          <svg
                            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <label className="flex items-center gap-2 flex-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={mainSelected}
                              onChange={() => toggleCategory(main.name)}
                              onClick={(e) => e.stopPropagation()}
                              className="mr-1"
                            />
                            <span className="text-sm font-medium text-gray-900">{main.name}</span>
                            {filteredSubs.length > 0 && (
                              <span className="text-xs text-gray-500">({filteredSubs.length}개 소분류)</span>
                            )}
                          </label>
                        </button>
                      </div>
                      {/* 소분류 */}
                      {isExpanded && filteredSubs.length > 0 && (
                        <div className="ml-6 space-y-1 mt-1">
                          {filteredSubs.map((sub) => (
                            <label key={sub.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                              <input
                                type="checkbox"
                                checked={formData.categories.includes(sub.name)}
                                onChange={() => toggleCategory(sub.name)}
                                className="mr-1"
                              />
                              <span className="text-xs text-gray-700">{sub.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-500">등록된 카테고리가 없습니다.</p>
            )}
          </div>
          {/* 선택된 카테고리 표시 */}
          {formData.categories.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-gray-600 mb-2">선택된 카테고리:</div>
              <div className="flex flex-wrap gap-2">
                {formData.categories.map((cat) => (
                  <span
                    key={cat}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
                  >
                    {cat}
                    <button
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className="text-blue-700 hover:text-blue-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 지역 - 검색 + 아코디언 */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            지역
          </label>
          <div className="mb-3">
            <input
              type="text"
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
              placeholder="지역 검색..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="border border-gray-300 rounded-lg p-4 max-h-96 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              <div className="space-y-2">
                {filteredCountries.map((country) => {
                  const regs = getRegionsByCountry(country.id);
                  const isExpanded = expandedCountries.has(country.id);
                  const searchLower = locationSearch.toLowerCase();
                  const filteredRegs = regs.filter(region =>
                    !locationSearch || region.name.toLowerCase().includes(searchLower)
                  );
                  const countrySelected = formData.locations.includes(country.name);

                  return (
                    <div key={country.id} className="border-b border-gray-200 last:border-b-0 pb-2 last:pb-0">
                      {/* 국가 */}
                      <div className="flex items-center gap-2 mb-1">
                        <button
                          type="button"
                          onClick={() => toggleCountry(country.id)}
                          className="flex items-center gap-2 flex-1 text-left hover:bg-gray-50 px-2 py-1 rounded"
                        >
                          <svg
                            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <label className="flex items-center gap-2 flex-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={countrySelected}
                              onChange={() => toggleLocation(country.name)}
                              onClick={(e) => e.stopPropagation()}
                              className="mr-1"
                            />
                            <span className="text-sm font-medium text-gray-900">{country.name}</span>
                            {filteredRegs.length > 0 && (
                              <span className="text-xs text-gray-500">({filteredRegs.length}개 지역)</span>
                            )}
                          </label>
                        </button>
                      </div>
                      {/* 지역 */}
                      {isExpanded && filteredRegs.length > 0 && (
                        <div className="ml-6 space-y-1 mt-1">
                          {filteredRegs.map((region) => (
                            <label key={region.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                              <input
                                type="checkbox"
                                checked={formData.locations.includes(region.name)}
                                onChange={() => toggleLocation(region.name)}
                                className="mr-1"
                              />
                              <span className="text-xs text-gray-700">{region.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-500">등록된 지역이 없습니다.</p>
            )}
          </div>
          {/* 선택된 지역 표시 */}
          {formData.locations.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-gray-600 mb-2">선택된 지역:</div>
              <div className="flex flex-wrap gap-2">
                {formData.locations.map((loc) => (
                  <span
                    key={loc}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs"
                  >
                    {loc}
                    <button
                      type="button"
                      onClick={() => toggleLocation(loc)}
                      className="text-green-700 hover:text-green-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 태그 */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            태그
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="태그 입력 후 Enter"
            />
            <button
              type="button"
              onClick={addTag}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              추가
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="text-blue-700 hover:text-blue-900"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* 예약 URL (파트너 링크) */}
        <div className="space-y-3">
          <label className="block text-xs font-medium text-gray-700">예약 URL</label>
          <datalist id="known-partner-names">
            {KNOWN_PARTNER_NAMES.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <div className="space-y-3">
            {formData.partnerLinks.map((link, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-2">
                {link.source === 'api' ? (
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-gray-900">{link.partner}</span>
                        <span className="text-[10px] bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded-sm font-bold">API 연동됨</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {link.priceDisplay || (link.price != null ? `${link.price.toLocaleString()}원` : '가격 정보 없음')}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => disconnectPartnerApi(index)}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs"
                      >
                        연동 해제
                      </button>
                      <button
                        type="button"
                        onClick={() => removePartnerLink(index)}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-xs"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        list="known-partner-names"
                        value={link.partner}
                        onChange={(e) => updatePartnerLink(index, 'partner', e.target.value)}
                        className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                        placeholder="파트너명"
                      />
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => updatePartnerLink(index, 'url', e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="예약 URL"
                      />
                      <button
                        type="button"
                        onClick={() => removePartnerLink(index)}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-xs shrink-0"
                      >
                        삭제
                      </button>
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={link.price != null ? String(link.price) : ''}
                      onChange={(e) => updatePartnerLink(index, 'price', e.target.value)}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                      placeholder="가격(선택) - 모르면 비워 두기"
                    />
                    {(link.price ?? 0) > 0 && (
                      <div className="flex gap-2 flex-wrap items-center">
                        <select aria-label="통화" value={link.priceCurrency || 'KRW'} onChange={(e) => updatePartnerLink(index, 'priceCurrency', e.target.value)} className="px-2 py-1 border border-gray-300 rounded-md text-xs bg-white">
                          {['KRW', 'JPY', 'USD', 'TWD', 'THB', 'VND', 'EUR', 'CNY'].map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input aria-label="가격 조건" value={link.priceConditions || ''} onChange={(e) => updatePartnerLink(index, 'priceConditions', e.target.value)} placeholder="가격 조건 (예: 성인 1인, 2인 이상)" className="flex-1 min-w-[180px] px-2 py-1 border border-gray-300 rounded-md text-xs" />
                        <span className="text-[11px] text-gray-500">{link.priceCheckedAt ? `${new Date(link.priceCheckedAt).toLocaleString('ko-KR')} 가격 확인` : '가격 확인 시각 모름'}</span>
                      </div>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      {PARTNER_API_OPTIONS.map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => openPartnerSearch(index, option.key)}
                          className="px-3 py-1.5 bg-cyan-50 text-cyan-700 rounded-lg hover:bg-cyan-100 text-xs font-medium"
                        >
                          {option.label} API로 검색해서 연결
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {partnerSearch?.index === index && (
                  <div className="mt-2 border border-cyan-200 bg-cyan-50/50 rounded-lg p-3 space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={partnerSearch.keyword}
                        onChange={(e) => setPartnerSearch({ ...partnerSearch, keyword: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), runPartnerSearch())}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs"
                        placeholder="검색 키워드 (예: 오사카 유니버설 스튜디오)"
                      />
                      <button
                        type="button"
                        onClick={runPartnerSearch}
                        disabled={partnerSearch.loading}
                        className="px-3 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-xs disabled:opacity-50"
                      >
                        {partnerSearch.loading ? '검색 중...' : '검색'}
                      </button>
                      <button
                        type="button"
                        onClick={closePartnerSearch}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs"
                      >
                        닫기
                      </button>
                    </div>
                    {partnerSearch.error && <p className="text-xs text-red-600">{partnerSearch.error}</p>}
                    {partnerSearch.results.length > 0 && (
                      <div className="space-y-1.5 max-h-64 overflow-y-auto">
                        {partnerSearch.results.map((result) => (
                          <button
                            key={result.externalId}
                            type="button"
                            onClick={() => selectPartnerSearchResult(result)}
                            className="w-full flex items-center justify-between gap-2 p-2 bg-white border border-gray-200 rounded-lg hover:border-cyan-300 text-left"
                          >
                            <span className="text-xs text-gray-800 truncate">{result.name}</span>
                            <span className="text-xs font-semibold text-gray-900 shrink-0">
                              {result.priceDisplay || (result.price !== undefined ? `${result.price.toLocaleString()}원` : '-')}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {link.url.trim() !== '' && (
                  <div className="border-t border-gray-100 pt-2 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-semibold text-gray-700">예약 가능 여부</span>
                      <select
                        aria-label="판매처 예약 가능 여부"
                        value={link.availability?.status || ''}
                        onChange={(e) => {
                          const status = e.target.value as PartnerAvailability['status'] | '';
                          if (!status) return updateAvailability(index, null);
                          updateAvailability(index, status === 'unavailable'
                            ? { status, reason: link.availability?.reason && UNAVAILABLE_REASONS[link.availability.reason] ? link.availability.reason : 'temporary_soldout', scope: link.availability?.scope || { whole: false, dates: [], options: [] } }
                            : status === 'unknown' ? { status, reason: 'not_checked' } : { status, reason: undefined }); // 범위·근거는 남겨 둠
                        }}
                        className="px-2 py-1 border border-gray-300 rounded-md text-xs bg-white"
                      >
                        {Object.entries(AVAILABILITY_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                      {link.availability?.status === 'unavailable' && (
                        <select aria-label="예약 불가 이유" value={link.availability.reason || 'temporary_soldout'} onChange={(e) => updateAvailability(index, { reason: e.target.value })} className="px-2 py-1 border border-gray-300 rounded-md text-xs bg-white">
                          {Object.entries(UNAVAILABLE_REASONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      )}
                      {link.availability?.status === 'unknown' && (
                        <select aria-label="확인 못 한 이유" value={link.availability.reason || 'not_checked'} onChange={(e) => updateAvailability(index, { reason: e.target.value })} className="px-2 py-1 border border-gray-300 rounded-md text-xs bg-white">
                          {Object.entries(UNKNOWN_REASONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      )}
                      {link.availability?.checkedAt && (
                        <span className="text-[11px] text-gray-500">{new Date(link.availability.checkedAt).toLocaleString('ko-KR')} 확인 · {link.availability.source}</span>
                      )}
                      {link.availability && (
                        <button type="button" onClick={() => updateAvailability(index, { checkedAt: 'now' })} className="px-2 py-1 border border-gray-300 rounded-md text-[11px] bg-white hover:bg-gray-50">
                          지금 다시 확인함
                        </button>
                      )}
                    </div>
                    {(link.availability?.status === 'unavailable' || link.availability?.status === 'available' || link.availability?.status === 'unknown') && (
                      <div className="space-y-1.5 pl-1">
                        <label className="flex items-center gap-1.5 text-[11px] text-gray-700">
                          <input type="checkbox" checked={Boolean(link.availability.scope?.whole)} onChange={(e) => updateAvailability(index, { scope: { ...link.availability!.scope, whole: e.target.checked } })} />
                          {link.availability.status === 'unavailable'
                            ? '상품 전체가 예약 불가 (특정 날짜·옵션만 품절이면 체크하지 말고 아래에 적어 주세요)'
                            : link.availability.status === 'available'
                              ? '상품 전체 기준으로 예약 가능을 확인 (특정 날짜·옵션만 확인했으면 체크하지 말고 아래에 적어 주세요)'
                              : '상품 전체를 확인하려 했음 (특정 날짜·옵션만 확인하려 했으면 아래에 적어 주세요)'}
                        </label>
                        {!link.availability.scope?.whole && (
                          <div className="flex flex-wrap gap-2">
                            <input aria-label="날짜 범위" placeholder={link.availability.status === 'unavailable' ? '품절 날짜 (쉼표로: 2026-12-24, 2026-12-25)' : '확인한 날짜 (쉼표로)'} defaultValue={(link.availability.scope?.dates || []).join(', ')} onBlur={(e) => updateAvailability(index, { scope: { ...link.availability!.scope, dates: splitList(e.target.value) } })} className="flex-1 min-w-[200px] px-2 py-1 border border-gray-300 rounded-md text-xs" />
                            <input aria-label="옵션·인원 범위" placeholder={link.availability.status === 'unavailable' ? '품절 옵션·인원 (쉼표로: 성인, 4/7)' : '확인한 옵션·인원 (쉼표로)'} defaultValue={(link.availability.scope?.options || []).join(', ')} onBlur={(e) => updateAvailability(index, { scope: { ...link.availability!.scope, options: splitList(e.target.value) } })} className="flex-1 min-w-[160px] px-2 py-1 border border-gray-300 rounded-md text-xs" />
                          </div>
                        )}
                        {link.availability.reason === 'sales_ended' && (
                          <input aria-label="판매 종료 근거" placeholder="판매 종료 근거 (판매처 공지·안내 문구) — 없으면 '판매 중지'로" defaultValue={link.availability.evidence || ''} onBlur={(e) => updateAvailability(index, { evidence: e.target.value.trim() })} className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs" />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addPartnerLink}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs"
          >
            파트너 추가
          </button>
        </div>

        {/* 옵션 */}
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isRecommended}
              onChange={(e) => setFormData({ ...formData, isRecommended: e.target.checked })}
              className="mr-2"
            />
            <span className="text-xs text-gray-700">추천 상품</span>
          </label>
          <fieldset className="space-y-1.5">
            <legend className="text-xs font-semibold text-gray-800 mb-1">공개 상태</legend>
            {formData.publishStatus === 'needs_classification' && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                분류 필요: 예전 '비활성' 상품을 옮길 때 초안인지 숨김인지 근거가 없었어요. 손님에게는 보이지 않아요. 아래에서 골라 주세요.
              </p>
            )}
            {PUBLISH_OPTIONS.map((option) => (
              <label key={option.value} className="flex items-start gap-2">
                <input
                  type="radio"
                  name="publishStatus"
                  value={option.value}
                  checked={formData.publishStatus === option.value}
                  onChange={() => setFormData({ ...formData, publishStatus: option.value })}
                  className="mt-0.5"
                />
                <span className="text-xs text-gray-700">
                  <b>{option.label}</b> — {option.help}
                </span>
              </label>
            ))}
          </fieldset>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-2 rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 font-medium"
          >
            {isEdit ? '수정하기' : '등록하기'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            취소
          </button>
        </div>
      </form>
    </div>
  );
}
